import "./index.scss";

import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import type { GridApi, GridReadyEvent, CellValueChangedEvent, FirstDataRenderedEvent } from "ag-grid-community";

import { useDataProcess } from "./hooks";
import { useColorScheme } from "@mui/joy";
import { GridQuickControl } from "./GridQuickControl";
import { paginationChangeControl } from "./utils";

ModuleRegistry.registerModules([AllCommunityModule]);

interface IDataGrid extends AgGridReactProps {
    gridData: any[];
    columnDefs?: any[];
    autoHeight?: boolean;
    onEdit?: (row: any) => void;
    animateRows?: boolean;
    hideHeader?: boolean;
    suppressQuickControl?: boolean;
}

interface PaginationState {
    currentPage: number;
    totalPage: number;
}

const GridComponent = (props: IDataGrid) => {
    const {
        gridData = [],
        columnDefs,
        autoHeight,
        onEdit,
        animateRows = true,
        hideHeader = false,
        suppressQuickControl = false,
        ...otherProps
    } = props;
    const [rowData, setRowData] = useState<any[]>(gridData);
    const [columnDefsLocal, setColumnDefsLocal] = useState<any[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [paginationControls, setPaginationControls] = useState<PaginationState>({
        currentPage: 0,
        totalPage: 0,
    });

    const { processInitialData } = useDataProcess();
    const { mode } = useColorScheme();
    const themeClass = mode === "dark" ? "ag-theme-custom-dark" : "ag-theme-custom-react";

    const columnDefsProcessed = useMemo(() => {
        if (columnDefs && columnDefs.length > 0) {
            return columnDefs;
        }
        return processInitialData(gridData);
    }, [columnDefs, gridData, processInitialData]);

    useEffect(() => {
        setColumnDefsLocal(columnDefsProcessed);
    }, [columnDefsProcessed]);

    useEffect(() => {
        setRowData(gridData);
    }, [gridData]);

    const handleGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
    }, []);

    const handleCellValueChange = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef, newValue } = event;
            const field = colDef.field as string;

            if (onEdit && field) {
                onEdit({ ...data, [field]: newValue });
            }
        },
        [onEdit]
    );

    const handlePaginationChanged = useCallback(() => {
        if (gridApi) {
            paginationChangeControl(gridApi, setPaginationControls);
        }
    }, [gridApi]);

    const gridProps = useMemo(() => {
        const baseProps = {
            animateRows,
            rowData,
            columnDefs: columnDefsLocal,
            domLayout: (autoHeight ? "autoHeight" : "normal") as "autoHeight" | "normal",
            onCellValueChanged: handleCellValueChange,
            onGridReady: handleGridReady,
            onPaginationChanged: handlePaginationChanged,
            pagination: true,
            paginationAutoPageSize: true,
            suppressPaginationPanel: true,
            ...otherProps,
        };

        if (hideHeader) {
            return { ...baseProps, headerHeight: 0 };
        }

        return baseProps;
    }, [
        animateRows,
        rowData,
        columnDefsLocal,
        autoHeight,
        handleCellValueChange,
        handleGridReady,
        handlePaginationChanged,
        hideHeader,
    ]);

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }} className={themeClass}>
            {suppressQuickControl ? null : (
                <GridQuickControl gridApi={gridApi} paginationControls={paginationControls} />
            )}
            {columnDefsLocal.length > 0 && <AgGridReact {...gridProps} />}
        </div>
    );
};

export default GridComponent;
